import StorageBase, { _originalLocalStorage } from "./storage.base";
import DutyStatusLog, { IAnnotation } from "../../model/DutyStatusLog";
import UserHosRuleSet from "../../model/UserHosRuleSet";
import { IAlertsAvailability, IDataStore, IImages } from "../../store/data/types";
import DVIRLog from "../../model/DVIRLog";
import { ITimeZoneInfo } from "../date";
import { IDailyOdometer } from "../../model/DutyStatusLogs";
import { IMessage, IRoute } from "../../model/Message";
import { IDriverChange } from "../../model/DriverChangeLog";
import { IDefect } from "../../model/Defect";
import { IShipment } from "../../model/Shipment";
import { StorageKeys, GetFunc, SetFunc, RemoveFunc } from "./storage";
import { ITrailerAttachment } from "../../model/TrailerAttachment";
import { ISuspiciousLogsSent } from "../../store/suspiciousLogsSent/types";
import { ICredentialsWithServer } from "../credentialsStore";
import { ICache } from "../../store/cache/types";
import { ISystemSettings } from "../../store/systemSettings/types";
import { defaultSystemSettings } from "../../store/systemSettings/reducers";
import { defaultVehicleHistory } from "../../store/vehicleHistory/reducers";
import { IVehicleHistoryStore, TVehicle } from "../../store/vehicleHistory/types";
import { DRIVE_STORAGE_PREFIX } from "../../store/storageConstants";

export type UpgraderFunc = (
    get: GetFunc,
    set: SetFunc,
    remove: RemoveFunc,
    clear: (removePersisted: boolean) => any
) => boolean;   // Should reload page

let isDriveKey = (key: string) => key.indexOf(DRIVE_STORAGE_PREFIX) === 0;

let storageUpgrades = (): [
    { [version: number]: UpgraderFunc },
    { [version: number]: UpgraderFunc },
    number[]
] => {
    let upgradesDict: {[version: number]: UpgraderFunc} = {};
    let downgradesDict: { [version: number]: UpgraderFunc } = {};
    let versionsToRunForEachUser: number[] = [];

    /**
     * Register a new "Storage Version" for Geotab Drive's data storage scheme. Typically this involves the movement or conversion of
     * one location or format of data storage into another and contains definitions for Upgrades/Downgrades, which represent said migrations/conversions
     * back and forth between the old/new schemas.
     *
     * @param version The new Version Number for Geotab Drive's data storage scheme. Corresponds to an increment of the value `APP_VERSION`
     * in `drive/app/constants.ts`.
     * @param runForEachUser A boolean value representing whether or not we want to run the storage upgrade once for each user currently signed
     * into the app.
     * @param upgradeMethod An UpgraderFunc representing the migration/conversion of the old data format/storage to a new format/location
     * @param downgradeMethod An UpgraderFunc representing the migration/conversion of the new data format/storage back to the old format/location. This
     * is necessary in the case that there is issues with the migration and we need to roll back the changes.
     *
     * @see UpgraderFunc @see DownGraderFunc @see storage.ts --> upgradeStorageToVersion @see constants.ts --> APP_VERSION
     */
    let registerVersion = (
        version: number,
        runForEachUser: boolean,
        upgradeMethod?: UpgraderFunc,
        downgradeMethod?: UpgraderFunc
    ) => {
        if (upgradeMethod) {
            upgradesDict[version] = upgradeMethod;
        }
        if (downgradeMethod) {
            downgradesDict[version] = downgradeMethod;
        }
        if (runForEachUser) {
            versionsToRunForEachUser.push(version);
        }
    };

    let safeRemoveAll = (get, set, _, clear) => {
        let importantStorage = [
            "@currentVehicle",
            "@currentTrailers"
        ];
        let importantValues = {};

        importantStorage.forEach(key => {
            importantValues[key] = get(key);
        });

        clear(true);

        importantStorage.forEach(key => {
            if (importantValues[key]) {
                set(key, importantValues[key]);
            }
        });

        // Force reload after execution
        return true;
    };

    //
    //  Register schema updates here
    //

    // Example
    //    registerVersion(0.02, (get, set, remove, clear) => {
    //        set("newTable", "Testing upgrades");
    //        return false;
    //    });

    //
    // Note
    //    any upgrades to global keys (starting with @) may be run multiple times for any given upgrade step
    //    upgrades run for each user in @users
    //    returning true will force the app to reload
    //
    // Make sure you update APP_VERSION in constants for each upgrade

    registerVersion(0.05, true, (_1, _2, remove) => {
        remove("users");
        return false;
    });

    registerVersion(0.07, true, (_1, _2, remove) => {
        remove("@referrer");
        return false;
    });

    registerVersion(0.08, true, safeRemoveAll);

    registerVersion(0.10, true, (_1, _2, remove) => {
        remove("feed_versions");
        remove("shipments");
        remove("trailers");

        // Force reload after execution
        return true;
    });

    registerVersion(0.11, true, (get, set) => {
        let currentTrailers = get<{version}[]>("@currentTrailers", true);
        if (currentTrailers && currentTrailers.length) {
            currentTrailers.forEach(trailer => {
                delete trailer.version;
            });
        }
        set("@currentTrailers", currentTrailers);
        return false;
    });

    registerVersion(0.13, true, () => {
        localStorage.removeItem("geotabAPI_credentials");
        localStorage.removeItem("geotabAPI_server");
        return false;
    });

    registerVersion(0.17, true, safeRemoveAll);

    registerVersion(0.18, true, (_1, _2, remove) => {
        remove("hos_cache_address");
        return false;
    });

    registerVersion(0.19, true, (get, set) => {
        let currentDvirLogs = get<[]>("dvir_logs", true);
        for (let key in currentDvirLogs) {
            if (currentDvirLogs.hasOwnProperty(key)) {
                let dvirLog = currentDvirLogs[key];
                if (dvirLog &&  typeof (dvirLog.device) === "object" && (!dvirLog.device.id || dvirLog.device.id === "")) {
                    // Remove invalid DVIR logs with empty device
                    delete currentDvirLogs[key];
                }
            }
        }
        set("dvir_logs", currentDvirLogs);
        return false;
    });

    registerVersion(0.20, true, (_1, _2, remove) => {
        remove("defects_vehicle");
        remove("defects_trailer");
        return false;
    });

    registerVersion(0.21, true, (get, set) => {
        let oldActiveUsers = get("@active", false);
        if (oldActiveUsers === null) {
            return false;
        }
        try {
            oldActiveUsers = JSON.parse(oldActiveUsers);
        } catch {}
        let newActiveUsers = Array.isArray(oldActiveUsers) ? oldActiveUsers : oldActiveUsers && oldActiveUsers.split("|").map(user => user.toLowerCase());
        set("@active", newActiveUsers);
        return false;
    }, (get, _1, _2) => {
        let oldActiveUsers = get("@active", false);
        if (oldActiveUsers === null) {
            return false;
        }
        try {
            oldActiveUsers = JSON.parse(oldActiveUsers);
        } catch {}
        oldActiveUsers = Array.isArray(oldActiveUsers) ? oldActiveUsers.join("|") : oldActiveUsers;
        return false;
    });

    registerVersion(0.22, true, undefined, () => {
        let usersInStorage = _originalLocalStorage.getItem("geotabDrive_@users");
        if (!usersInStorage) {
            return false;
        }

        let parsedUsers = JSON.parse(usersInStorage);
        for (let user in parsedUsers) {
            let userPrefix = `${ "geotabDrive_" }${ parsedUsers[user]["id"] }`;
            let hosLogs = _originalLocalStorage.getItem(`${ userPrefix }${ "_hos_logs" }`);

            if (hosLogs && (hosLogs.length > 0) && hosLogs.indexOf("status") === -1) {
                _originalLocalStorage.setItem(`${ userPrefix }${ "_logsCompressed" }`, "true");
            }
        }
        return false;
    });

    registerVersion(0.23, true, (_1, _2, remove) => {
        remove("defects_device");
        remove("defects_trailer");
        return false;
    });

    registerVersion(0.24, true, (_1, _2, remove) => {
        // Reset caches that had properties added
        remove("cache_trailer");
        remove("cache_device");

        // New permissions were added: InspectDVIR and CertifyDVIR
        remove("@groupSecurityId");
        remove("acl");
        return false;
    });

    registerVersion(0.25, true, (_1, _2, remove) => {
        // Force re-login to prevent errors when user doesn't have timezone info populated
        remove("@active");
        return false;
    });

    registerVersion(0.26, true, (get, set) => {
        // Update the way we store NoDeviceId and NoUserId on HosLogs
        ["hos_logs", "system_hos_logs"].forEach(key => {
            let hosLogs = get<{}>(key, true);
            let newLogs = {};
            for (let id in hosLogs) {
                let l = hosLogs[id];
                if (l?.device === "NoDeviceId") {
                    l.device = { id: "NoDeviceId" };
                }
                if (l?.driver === "NoUserId") {
                    l.driver = { id: "NoUserId" };
                }
                newLogs[id] = l;
            }
            set(key, newLogs);
        });
        return false;
    }, (get, set) => {
        // Downgrade
        ["hos_logs", "system_hos_logs"].forEach(key => {
            let hosLogs = get<{}>(key, true);
            let newLogs = {};
            for (let id in hosLogs) {
                let l = hosLogs[id];
                if (l?.device?.id === "NoDeviceId") {
                    l.device = "NoDeviceId";
                }
                if (l?.driver?.id === "NoUserId") {
                    l.driver = "NoUserId";
                }
                newLogs[id] = l;
            }
            set(key, newLogs);
        });
        return false;
    });

    registerVersion(0.28, true, (get, set, remove) => {
        //Upgrade
        if (!StorageBase.keyExist("@users")) {
            return false;
        }

        set("@store_users", get("@users", false));
        remove("@users");
        return false;
    }, (get, set, remove) => {
        // Downgrade
        if (!StorageBase.keyExist("@store_users")) {
            return false;
        }

        set("@users", get("@store_users", false));
        remove("@store_users");
        return false;
    });

    registerVersion(0.281, true, (get, set) => {
        //Upgrade
        let currentDvirLogs = get<[]>("dvir_logs", true);
        for (let dvirLogId in currentDvirLogs) {
            if (currentDvirLogs.hasOwnProperty(dvirLogId)) {
                let dvirLog = currentDvirLogs[dvirLogId];
                if (dvirLog.device === "NoDeviceId") {
                    dvirLog.device = { "id": "NoDeviceId" };
                }
                if (dvirLog.trailer === "NoTrailerId" || Object.keys(dvirLog.trailer).length === 0) {
                    dvirLog.trailer = { "id": "NoTrailerId" };
                }
                Object.keys(dvirLog).map(key => {
                    if (dvirLog[key] === null) {
                        dvirLog[key] = undefined;
                    }
                });
            }
        }
        set("dvir_logs", currentDvirLogs);
        return false;
    }, (get, set) => {
        let currentDvirLogs = get<[]>("dvir_logs", true);
        for (let dvirLogId in currentDvirLogs) {
            if (currentDvirLogs.hasOwnProperty(dvirLogId)) {
                let dvirLog = currentDvirLogs[dvirLogId];
                if (dvirLog.device.id === "NoDeviceId") {
                    dvirLog.device = "NoDeviceId";
                }
                if (dvirLog.trailer.id === "NoTrailerId") {
                    dvirLog.trailer = "NoTrailerId";
                }
                Object.keys(dvirLog).map(key => {
                    if (dvirLog[key] === undefined) {
                        dvirLog[key] = null;
                    }
                });
            }
        }
        set("dvir_logs", currentDvirLogs);
        return false;
    });

    //geotabDrive_b6_hos_logs <=> geotabDrive_@store_userHosLogs
    //geotabDrive_b6_system_hos_logs <=> geotabDrive_@store_systemHosLogs
    registerVersion(0.29, true, (get, set, remove) => {
        //Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("hos_logs"))) {
            return false;
        }

        let storeUserHosLogs: IIdDictionary<IIdDictionary<DutyStatusLog>> = {};
        let storeSystemHosLogs: IIdDictionary<IIdDictionary<DutyStatusLog>> = {};

        Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.includes("system_hos_logs")){
                const userId: string = storageKey.split("_")[1];
                const systemHosLogs = get("system_hos_logs", true, userId) as IIdDictionary<DutyStatusLog>;
                Object.keys(systemHosLogs).forEach(logId => {
                    if (!storeSystemHosLogs[userId]){
                        storeSystemHosLogs[userId] = {};
                    }
                    storeSystemHosLogs[userId][logId] = systemHosLogs[logId];
                });
            }

            if (storageKey.includes("hos_logs") && !storageKey.includes("system_hos_logs")){
                const userId: string = storageKey.split("_")[1];
                const userHosLogs = get("hos_logs", true, userId) as IIdDictionary<DutyStatusLog>;
                Object.keys(userHosLogs).forEach(logId => {
                    if (!storeUserHosLogs[userId]){
                        storeUserHosLogs[userId] = {};
                    }
                    storeUserHosLogs[userId][logId] = userHosLogs[logId];
                });
            }
        });

        set("geotabDrive_@store_userHosLogs", storeUserHosLogs, undefined, true);
        Object.keys(storeUserHosLogs).forEach(userId => {
            remove("hos_logs", userId);
        });

        set("geotabDrive_@store_systemHosLogs", storeSystemHosLogs, undefined, true);
        Object.keys(storeSystemHosLogs).forEach(userId => {
            remove("system_hos_logs", userId);
        });

        return false;
    }, (get, set, remove) => {
        // Downgrade
        if (!Object.keys(localStorage).find(key => key.includes("HosLogs"))) {
            return false;
        }

        let userHosLogs = get("@store_userHosLogs", true) as IIdDictionary<IIdDictionary<DutyStatusLog>>;
        let systemHosLogs = get("@store_systemHosLogs", true) as IIdDictionary<IIdDictionary<DutyStatusLog>>;

        Object.keys(userHosLogs).forEach((userId) => {
            set("hos_logs", userHosLogs[userId], userId);
        });
        remove("@store_userHosLogs");

        Object.keys(systemHosLogs).forEach((userId) => {
            set("system_hos_logs", systemHosLogs[userId], userId);
        });
        remove("@store_systemHosLogs");

        return false;
    });

    registerVersion(0.30, true, (get, set, remove) => {
        //Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("user_hos_rule_set"))) {
            return false;
        }

        let storeUserHosRuleSets: IIdDictionary<IIdDictionary<UserHosRuleSet>> = {};

        Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.includes("user_hos_rule_set")){
                const userId: string = storageKey.split("_")[1];
                const ruleSets = get("user_hos_rule_set", true, userId) as IIdDictionary<UserHosRuleSet>;
                Object.keys(ruleSets).forEach(logId => {
                    if (!storeUserHosRuleSets[userId]){
                        storeUserHosRuleSets[userId] = {};
                    }
                    storeUserHosRuleSets[userId][logId] = ruleSets[logId];
                });
            }
        });

        set("geotabDrive_@store_userHosRuleSets", storeUserHosRuleSets, undefined, true);
        Object.keys(storeUserHosRuleSets).forEach(userId => {
            remove("user_hos_rule_set", userId);
        });

        return false;
    }, (get, set, remove) => {
        // Downgrade
        if (!Object.keys(localStorage).find(key => key.includes("@store_userHosRuleSets"))) {
            return false;
        }

        let userHosRuleSets = get("@store_userHosRuleSets", true) as IIdDictionary<IIdDictionary<UserHosRuleSet>>;

        Object.keys(userHosRuleSets).forEach((userId) => {
            set("user_hos_rule_set", userHosRuleSets[userId], userId);
        });
        remove("@store_userHosRuleSets");

        return false;
    });

    // geotabDrive_@debug --> geotabDrive_@store_data::debug
    registerVersion(0.31, true, (get, set, remove) => {
        // Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("@debug"))) {
            return false;
        }

        set(
            "@store_data",
            {
                ...get(
                    "@store_data",
                    true
                ),
                debugMode: get(
                    "@debug",
                    true
                )
            }
        );
        remove("@debug");

        return false;
    }, (get, set, _1) => {
        // Downgrade
        let { debugMode, ...storeData } = get("@store_data", true) as {debugMode: boolean; storeData: IDataStore};
        set("@debug", debugMode);
        set("@store_data", storeData);

        return false;
    });

    // geotabDrive_<userId>_boundaryLogDate --> geotabDrive_@store_data::<userId>::boundaryLogDate
    registerVersion(0.32, true, (get, set, remove) => {
        // Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("boundaryLogDate"))) {
            return false;
        }

        let storeBoundaryLogDates: IIdDictionary<string> = {};

        Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.includes("boundaryLogDate")) {
                const userId: string = storageKey.split("_")[1];
                const boundaryLogDate = get("boundaryLogDate", false, userId) as string;
                storeBoundaryLogDates[userId] = boundaryLogDate;
            }
        });

        set(
            "@store_data",
            {
                ...get(
                    "@store_data",
                    true
                ),
                boundaryLogDate: storeBoundaryLogDates
            }
        );

        Object.keys(storeBoundaryLogDates).forEach(userId => {
            remove("boundaryLogDate", userId);
        });

        return false;
    }, (get, set, _1) => {
        // Downgrade
        let { boundaryLogDate, ...storeData } = get("@store_data", true) as { boundaryLogDate: IIdDictionary<string>; storeData: IDataStore };

        if (boundaryLogDate && Object.keys(boundaryLogDate).length > 0) {
            Object.keys(boundaryLogDate).forEach(userId => {
                set("boundaryLogDate", boundaryLogDate[userId], userId);
            });
        }

        set("@store_data", storeData);
        return false;
    });

    //geotabDrive_b6_dvir_logs <=> geotabDrive_@store_dvirLogs
    registerVersion(0.33, true, (get, set, remove) => {
        //Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("dvir_logs"))) {
            return false;
        }

        let storeDvirLogs: IIdDictionary<IIdDictionary<DVIRLog>> = {};

        Object.keys(localStorage).forEach((storageKey) => {
            if (isDriveKey(storageKey) && storageKey.includes("dvir_logs")){
                const userId: string = storageKey.split("_")[1];
                const dvirLogs = get("dvir_logs", true, userId) as IIdDictionary<DVIRLog>;
                Object.keys(dvirLogs).forEach(logId => {
                    if (!storeDvirLogs[userId]){
                        storeDvirLogs[userId] = {};
                    }
                    storeDvirLogs[userId][logId] = dvirLogs[logId];
                });
            }
        });

        set("geotabDrive_@store_dvirLogs", storeDvirLogs, undefined, true);
        Object.keys(storeDvirLogs).forEach(userId => {
            remove("dvir_logs", userId);
        });

        return false;
    }, (get, set, remove) => {
        // Downgrade
        let storeKey = `${ DRIVE_STORAGE_PREFIX }@store_dvirLogs`;
        if (!Object.keys(localStorage).find(key => key === storeKey)) {
            return false;
        }

        let dvirLogs = get("@store_dvirLogs", true) as IIdDictionary<IIdDictionary<DVIRLog>>;

        Object.keys(dvirLogs).forEach((userId) => {
            set("dvir_logs", dvirLogs[userId], userId);
        });
        remove("@store_dvirLogs");

        return false;
    });

    // geotabDrive_<userId>_dtcNextRun --> geotabDrive_@store_data::<userId>::dtcNextRun
    registerVersion(0.34, true, (get, set, remove) => {
        // Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("dtcNextRun"))) {
            return false;
        }

        let storeDtcNextRuns: IIdDictionary<string> = {};

        Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.includes("dtcNextRun")) {
                const userId: string = storageKey.split("_")[1];
                const dtcNextRun = get("dtcNextRun", false, userId) as string;
                storeDtcNextRuns[userId] = dtcNextRun;
            }
        });

        set(
            "@store_data",
            {
                ...get(
                    "@store_data",
                    true
                ),
                dtcNextRun: storeDtcNextRuns
            }
        );

        Object.keys(storeDtcNextRuns).forEach(userId => {
            remove("dtcNextRun", userId);
        });

        return false;
    }, (get, set, _1) => {
        // Downgrade
        let { dtcNextRun, ...storeData } = get("@store_data", true) as { dtcNextRun: IIdDictionary<string>; storeData: IDataStore };

        if (dtcNextRun && Object.keys(dtcNextRun).length > 0) {
            Object.keys(dtcNextRun).forEach(userId => {
                set("dtcNextRun", dtcNextRun[userId], userId);
            });
        }

        set("@store_data", storeData);
        return false;
    });

    registerVersion(0.35, true, (get, set, remove) => {
        //Upgrade

        // Make sure not to run the upgrade if it already ran for one user
        if (!Object.keys(localStorage).find(key => isDriveKey(key) && key.endsWith("timeZoneInfo") && !key.includes(StorageKeys.TIME_ZONE_INFO_KEY))) {
            return false;
        }

        let storetimeZoneInfo: IIdDictionary<ITimeZoneInfo> = {};
        Object.keys(localStorage).forEach((storageKey) => {
            if (isDriveKey(storageKey) && storageKey.endsWith("timeZoneInfo")){
                const userId: string = storageKey.split("_")[1];
                const timeZoneInfo = get("timeZoneInfo", true, userId) as ITimeZoneInfo;
                storetimeZoneInfo[userId] = timeZoneInfo;
            }
        });
        Object.keys(storetimeZoneInfo).forEach(userId => {
            remove("timeZoneInfo", userId);
        });
        set("geotabDrive_@store_timeZoneInfo", storetimeZoneInfo, undefined, true);

        return false;
    }, (get, set, remove) => {
        // Downgrade
        if (!Object.keys(localStorage).find(key => isDriveKey(key) && key.endsWith("@store_timeZoneInfo"))) {
            return false;
        }

        let storetimeZoneInfo = get("@store_timeZoneInfo", true) as IIdDictionary<ITimeZoneInfo>;
        remove("@store_timeZoneInfo");

        Object.keys(storetimeZoneInfo).forEach((userId) => {
            set("timeZoneInfo", storetimeZoneInfo[userId], userId);
        });

        return false;
    });

    registerVersion(0.36, true, (get, set, remove) => {
        //Upgrade
        if (!Object.keys(localStorage).find(key => isDriveKey(key) && key.endsWith("dailyOdometer"))) {
            return false;
        }

        let storeDailyOdometer: IIdDictionary<IDailyOdometer> = {};

        Object.keys(localStorage).forEach((storageKey) => {
            if (isDriveKey(storageKey) && storageKey.includes("dailyOdometer")){
                const userId: string = storageKey.split("_")[1];
                const dailyOdometer = get("dailyOdometer", true, userId) as IDailyOdometer;
                Object.keys(dailyOdometer).forEach(logId => {
                    if (!storeDailyOdometer[userId]){
                        storeDailyOdometer[userId] = {};
                    }
                    storeDailyOdometer[userId][logId] = dailyOdometer[logId];
                });
            }
        });

        set("geotabDrive_@store_dailyOdometer", storeDailyOdometer, undefined, true);
        Object.keys(storeDailyOdometer).forEach(userId => {
            remove("dailyOdometer", userId);
        });

        return false;
    }, (get, set, remove) => {
        // Downgrade
        if (!Object.keys(localStorage).find(key => isDriveKey(key) && key.endsWith("@store_dailyOdometer"))) {
            return false;
        }

        let storeDailyOdometer = get("@store_dailyOdometer", true) as IIdDictionary<IDailyOdometer>;

        Object.keys(storeDailyOdometer).forEach((userId) => {
            set("dailyOdometer", storeDailyOdometer[userId], userId);
        });
        remove("@store_dailyOdometer");

        return false;
    });

    // geotabDrive_<userId>_nightMode --> geotabDrive_@store_data::nightMode
    registerVersion(0.37, true, (_1, _2, remove) => {
        // Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("nightMode") && isDriveKey(key))) {
            return false;
        }

        // Remove all stored night modes and just set @store_data value to false as default
        Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.includes("nightMode") && isDriveKey(storageKey)) {
                const userId: string = storageKey.split("_")[1];
                remove("nightMode", userId);
            }
        });

        return false;
    }, (get, set, _1) => {
        // Downgrade - just remove nightMode from @store_data
        let { nightMode, ...storeData } = get("@store_data", true) as { nightMode: boolean; storeData: IDataStore };
        set("@store_data", storeData);
        return false;
    });

    registerVersion(0.38, true, (get, set, remove) => {
        //Upgrade
        if (!Object.keys(localStorage).find(key => isDriveKey(key) && key.endsWith("messages"))) {
            return false;
        }

        let messages: IIdDictionary<IIdDictionary<IMessage>> = {};

        Object.keys(localStorage).forEach((storageKey) => {
            if (isDriveKey(storageKey) && storageKey.endsWith("messages")){
                const userId: string = storageKey.split("_")[1];
                const userMessages = get("messages", true, userId) as IIdDictionary<IMessage>;
                Object.keys(userMessages).forEach(msgId => {
                    if (!messages[userId]){
                        messages[userId] = {};
                    }
                    messages[userId][msgId] = userMessages[msgId];
                });
            }
        });

        set("geotabDrive_@store_messages", messages, undefined, true);
        Object.keys(messages).forEach(userId => {
            remove("messages", userId);
        });

        return false;
    }, (get, set, remove) => {
        // Downgrade
        let storeKey = `${ DRIVE_STORAGE_PREFIX }@store_messages`;
        if (!Object.keys(localStorage).find(key => key === storeKey)) {
            return false;
        }

        let messages = get("@store_messages", true) as IIdDictionary<IIdDictionary<IMessage>>;

        Object.keys(messages).forEach((userId) => {
            set("messages", messages[userId], userId);
        });
        remove("@store_messages");

        return false;
    });

    // geotabDrive_<userId>_acl --> geotabDrive_@store_data::<userId>::acl
    registerVersion(0.39, true, (get, set, remove) => {
        // Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("acl") && isDriveKey(key))) {
            return false;
        }

        let storeAcls: IIdDictionary<string[]> = {};

        Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.includes("acl") && isDriveKey(storageKey)) {
                const userId: string = storageKey.split("_")[1];
                const acl = get("acl", true, userId) as string[];
                storeAcls[userId] = acl;
            }
        });

        set(
            "@store_data",
            {
                ...get(
                    "@store_data",
                    true
                ),
                acl: storeAcls
            }
        );

        Object.keys(storeAcls).forEach(userId => {
            remove("acl", userId);
        });

        return false;
    }, (get, set, _1) => {
        // Downgrade
        let { acl, ...storeData } = get("@store_data", true) as { acl: IIdDictionary<string[]>; storeData: IDataStore };

        if (acl && Object.keys(acl).length > 0) {
            Object.keys(acl).forEach(userId => {
                set("acl", acl[userId], userId);
            });
        }

        set("@store_data", storeData);
        return false;
    });

    // geotabDrive_@lastDatabase --> geotabDrive_@store_data::lastDatabase
    registerVersion(0.40, true, (get, set, remove) => {
        // Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("@lastDatabase"))) {
            return false;
        }

        let lastDatabaseStored = get("@lastDatabase", false);
        set(
            "@store_data",
            {
                ...get(
                    "@store_data",
                    true
                ),
                lastDatabase: lastDatabaseStored
            }
        );
        remove("@lastDatabase");

        return false;
    }, (get, set, _1) => {
        // Downgrade
        let { lastDatabase, ...storeData } = get("@store_data", true) as {lastDatabase: string; storeData: IDataStore};
        lastDatabase && set("@lastDatabase", lastDatabase);
        set("@store_data", storeData);

        return false;
    });

    registerVersion(0.41, true, (_1, _2, remove) => {
        let cacheAddressUserIds: string[] = [];
        let cacheAddressDvirUserIds: string[] = [];

        Object.keys(localStorage).forEach((storageKey) => {
            if (isDriveKey(storageKey)){
                if (storageKey.includes("cache_address")) {
                    const userId: string = storageKey.split("_")[1];
                    cacheAddressUserIds.push(userId);
                }
                if (storageKey.includes("cache_address_dvir")) {
                    const userId: string = storageKey.split("_")[1];
                    cacheAddressDvirUserIds.push(userId);
                }
            }
        });
        cacheAddressUserIds.forEach(userId => {
            remove("cache_address", userId);
        });

        cacheAddressDvirUserIds.forEach(userId => {
            remove("cache_address_dvir", userId);
        });

        return false;
    });

    // geotabDrive_referrer --> geotabDrive_@store_data::referrer
    registerVersion(0.42, true, (get, set, remove) => {
        // Upgrade. Remove referrer from localstorage
        Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.includes("@referrer") && isDriveKey(storageKey)) {
                let referrer = get("@referrer", false);
                set(
                    "@store_data",
                    {
                        ...get(
                            "@store_data",
                            true
                        ),
                        referrer: referrer
                    }
                );
                remove("@referrer");
            }
        });

        return false;
    }, (get, set, _1) => {
        // Downgrade - just remove referrer from @store_data
        let { referrer, ...storeData } = get("@store_data", true) as { referrer: string; storeData: IDataStore };
        set("@store_data", storeData);
        referrer && set("@referrer", referrer);
        return false;
    });

    // geotabDrive_@lastUser --> geotabDrive_@store_data::lastUser & geotabDrive_@lastServer --> geotabDrive_@store_data::lastServer
    registerVersion(0.43, true, (get, set, remove) => {
        // Upgrade
        if (Object.keys(localStorage).find(key => isDriveKey(key) && key.endsWith("@lastUser"))) {
            set(
                "@store_data",
                {
                    ...get(
                        "@store_data",
                        true
                    ),
                    lastUser: get(
                        "@lastUser",
                        false
                    )
                }
            );
            remove("@lastUser");
        }

        if (Object.keys(localStorage).find(key => isDriveKey(key) && key.endsWith("@lastServer"))) {
            set(
                "@store_data",
                {
                    ...get(
                        "@store_data",
                        true
                    ),
                    lastServer: get(
                        "@lastServer",
                        false
                    )
                }
            );
            remove("@lastServer");
        }

        return false;
    }, (get, set, _1) => {
        // Downgrade
        let { lastUser, lastServer, ...storeData } = get("@store_data", true) as {lastUser: string; lastServer: string; storeData: IDataStore};
        if (lastUser) {
            set("@lastUser", lastUser);
        }
        if (lastServer) {
            set("@lastServer", lastServer);
        }
        set("@store_data", storeData);
        return false;
    });

    // geotabDrive_@input_Hints --> geotabDrive_@store_data::inputHints
    registerVersion(0.44, true, (get, set, remove) => {
        // Upgrade

        let storeInputHints: IIdDictionary<string[]> = {};

        Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.includes("input_hints") && isDriveKey(storageKey)) {
                const userId: string = storageKey.split("_")[1];
                storeInputHints[userId] = get("input_hints", true, userId) as string[];
            }
        });

        set(
            "@store_data",
            {
                ...get(
                    "@store_data",
                    true
                ),
                inputHints: storeInputHints
            }
        );

        Object.keys(storeInputHints).forEach(userId => {
            remove("input_hints", userId);
        });

        return false;
    }, (get, set, _1) => {
        // Downgrade
        let { inputHints, ...storeData } = get("@store_data", true) as { inputHints: IIdDictionary<string[]>; storeData: IDataStore };

        if (inputHints && Object.keys(inputHints).length > 0) {
            Object.keys(inputHints).forEach(userId => {
                set("input_hints", inputHints[userId], userId);
            });
        }

        set("@store_data", storeData);
        return false;
    });

    // geotabDrive_@groupSecurityId --> geotabDrive_@store_data::groupSecurityId
    registerVersion(0.45, true, (get, set, remove) => {
        //Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("@groupSecurityId") && isDriveKey(key))) {
            return false;
        }

        const groupSecurityIdStored = get("@groupSecurityId", true);
        set(
            "@store_data",
            {
                ...get(
                    "@store_data",
                    true
                ),
                groupSecurityId: groupSecurityIdStored
            }
        );
        remove("@groupSecurityId");
        return false;
    }, (get, set, _1) => {
        // Downgrade
        let { groupSecurityId, ...storeData } = get("@store_data", true) as {groupSecurityId: string; storeData: IDataStore};
        groupSecurityId && set("@groupSecurityId", groupSecurityId);
        set("@store_data", storeData);

        return false;
    });

    /**
     * feed_versions -> @store_feedVersions
     * @store_feedVersions -> feed_versions
     */
     registerVersion(0.46, true, (get, set, remove) => {
        //Upgrade
        let storeFeedVersions: IIdDictionary<IIdDictionary<string | IIdDictionary<string>>> | null = null;

        Object.keys(localStorage).forEach(storageKey => {
            if (isDriveKey(storageKey) && storageKey.includes("feed_versions")) {
                if (!storeFeedVersions) {
                    storeFeedVersions = {};
                }
                const userId: string = storageKey.split("_")[1];
                storeFeedVersions[userId] = get("feed_versions", true, userId) as IIdDictionary<string | IIdDictionary<string>>;
            }
        });

        if (storeFeedVersions) {
            set("geotabDrive_@store_feedVersions", storeFeedVersions, undefined, true);

            Object.keys(storeFeedVersions).forEach(userId => {
                remove("feed_versions", userId);
            });
        }

         return false;
     }, (get, set, remove) => {
         // Downgrade
         if (!Object.keys(localStorage).find(key => isDriveKey(key) && key.includes("@store_feedVersions"))) {
             return false;
         }

         let feedVersions = get("@store_feedVersions", true) as IIdDictionary<IIdDictionary<string | IIdDictionary<string>>>;

         feedVersions && Object.keys(feedVersions).length > 0 && Object.keys(feedVersions).forEach((userId) => {
             set("feed_versions", feedVersions[userId], userId);
         });
         remove("@store_feedVersions");

         return false;
     });

    registerVersion(0.47, true, (get, set, remove) => {
        //Upgrade
        let storeDriverChanges: IIdDictionary<IDriverChange[]> | null = null;

        Object.keys(localStorage).forEach((storageKey) => {
            if (isDriveKey(storageKey) && storageKey.includes("driver_change")){
                if (storeDriverChanges === null) {
                    storeDriverChanges = {};
                }
                const userId: string = storageKey.split("_")[1];
                const driverChanges = get("driver_change", true, userId) as IDriverChange[];
                storeDriverChanges[userId] = driverChanges;
            }
        });

        storeDriverChanges && set("geotabDrive_@store_driverChanges", storeDriverChanges, undefined, true);
        storeDriverChanges && Object.keys(storeDriverChanges).forEach(userId => {
            remove("driver_change", userId);
        });

        return false;
    }, (get, set, remove) => {
        // Downgrade
        if (!Object.keys(localStorage).find(key => isDriveKey(key) && key.includes("@store_driverChanges"))) {
            return false;
        }

        let storeDriverChanges = get("@store_driverChanges", true) as IIdDictionary<IDriverChange[]>;

        storeDriverChanges && Object.keys(storeDriverChanges).forEach((userId) => {
            set("driver_change", storeDriverChanges[userId], userId);
        });
        remove("@store_driverChanges");

        return false;
    });

    /**
     * geotabDrive_{userid}_defectlists_trailer <---> geotabDrive_@store_trailerDefectLists
     * geotabDrive_{userid}_defectlists_device <---> geotabDrive_@store_deviceDefectLists
     */
    registerVersion(0.48, true, (get, set, remove) => {
        //Upgrade
        let storeDeviceDefectLists: IIdDictionary<IIdDictionary<IDefect[]>> | null = null;
        let storeTrailerDefectLists: IIdDictionary<IIdDictionary<IDefect[]>> | null = null;

        Object.keys(localStorage).forEach(storageKey => {
            if (isDriveKey(storageKey)) {
                if (storageKey.includes("defectlists_device")) {
                    if (!storeDeviceDefectLists) {
                        storeDeviceDefectLists = {};
                    }
                    const userId = storageKey.split("_")[1];
                    storeDeviceDefectLists[userId] = get("defectlists_device", true, userId) as IIdDictionary<IDefect[]>;
                } else if (storageKey.includes("defectlists_trailer")) {
                    if (!storeTrailerDefectLists) {
                        storeTrailerDefectLists = {};
                    }
                    const userId = storageKey.split("_")[1];
                    storeTrailerDefectLists[userId] = get("defectlists_trailer", true, userId) as IIdDictionary<IDefect[]>;
                }
            }
        });

        if (storeDeviceDefectLists) {
            set("geotabDrive_@store_deviceDefectLists", storeDeviceDefectLists, undefined, true);

            Object.keys(storeDeviceDefectLists).forEach(userId => {
                remove("defectlists_device", userId);
            });
        }

        if (storeTrailerDefectLists) {
            set("geotabDrive_@store_trailerDefectLists", storeTrailerDefectLists, undefined, true);

            Object.keys(storeTrailerDefectLists).forEach(userId => {
                remove("defectlists_trailer", userId);
            });
        }

        return false;
    }, (get, set, remove) => {
        // Downgrade
        if (!Object.keys(localStorage).find(key => isDriveKey(key) && (key.includes("@store_deviceDefectLists") || key.includes("@store_trailerDefectLists")))) {
            return false;
        }

        let deviceDefectList = get("@store_deviceDefectLists", true) as IIdDictionary<IIdDictionary<IDefect[]>>;
        let trailerDefectList = get("@store_trailerDefectLists", true) as IIdDictionary<IIdDictionary<IDefect[]>>;

        deviceDefectList && Object.keys(deviceDefectList).length > 0 && Object.keys(deviceDefectList).forEach((userId) => {
            set("defectlists_device", deviceDefectList[userId], userId);
        });
        trailerDefectList && Object.keys(trailerDefectList).length > 0 && Object.keys(trailerDefectList).forEach((userId) => {
            set("defectlists_trailer", trailerDefectList[userId], userId);
        });
        remove("@store_deviceDefectLists");
        remove("@store_trailerDefectLists");

        return false;
    });

    // geotabDrive_<userId>_hos_annotations --> geotabDrive_@store_hos_annotations:<userId>::hos_annotations
    registerVersion(0.49, true, (get, set, remove) => {
        //Upgrade
        const storageKey = "hos_annotations";
        let storeAnnotations: IIdDictionary<IIdDictionary<IAnnotation>> = {};
        Object.keys(localStorage).forEach((key) => {
            if (key.includes(storageKey) && isDriveKey(key)) {
                const userId: string = key.split("_")[1];
                const annotations = get(storageKey, true, userId) as IIdDictionary<IAnnotation>;
                if (annotations && Object.keys(annotations).length > 0) {
                    Object.keys(annotations).forEach(logId => {
                        if (!storeAnnotations[userId]) {
                            storeAnnotations[userId] = {};
                        }
                        storeAnnotations[userId][logId] = annotations[logId];
                    });
                }
            }
        });
        set(`geotabDrive_${ StorageKeys.HOSANNOTATIONS_KEY }`, storeAnnotations, undefined, true);
        Object.keys(storeAnnotations).forEach(userId => {
            remove(storageKey, userId);
        });

        return false;
    }, (get, set, remove) => {
        // Downgrade
        if (!Object.keys(localStorage).find(key => key.includes(StorageKeys.HOSANNOTATIONS_KEY))) {
            return false;
        }

        let storeAnnotations = get(StorageKeys.HOSANNOTATIONS_KEY, true) as IIdDictionary<IIdDictionary<IAnnotation>>;
        if (storeAnnotations && Object.keys(storeAnnotations).length > 0) {
            Object.keys(storeAnnotations).forEach((userId) => {
                set("hos_annotations", storeAnnotations[userId], userId);
            });
            remove(StorageKeys.HOSANNOTATIONS_KEY);
        }

        return false;
    });

    // geotabDrive_<userId>_routes --> geotabDrive_@store_data_routes
    registerVersion(0.50, true, (get, set, remove) => {
        //Upgrade
        let routes: IIdDictionary<IIdDictionary<IRoute[]>> | null = null;
        Object.keys(localStorage).forEach(storageKey => {
            if (isDriveKey(storageKey)) {
                if (storageKey.includes("routes")) {
                    if (!routes) {
                        routes = {};
                    }
                    const userId = storageKey.split("_")[1];
                    routes[userId] = get("routes", true, userId) as IIdDictionary<IRoute[]>;
                }
            }
        });

        if (routes) {
            set("geotabDrive_@store_routes", routes, undefined, true);
            Object.keys(routes).forEach(id => {
                remove("routes", id);
            });
        }

        return false;
    }, (get, set, remove) => {
        // Downgrade
        if (!Object.keys(localStorage).find(key => key.includes("@store_routes") && isDriveKey(key))) {
            return false;
        }

        let routes = get("@store_routes", true) as IIdDictionary<IIdDictionary<IRoute>>;

        Object.keys(routes).length > 0 && Object.keys(routes).forEach((userId) => {
            set("routes", routes[userId], userId);
        });
        remove("@store_routes");

        return false;
    });

    // geotabDrive_@offline_error --> geotabDrive_@error::offline_error
    registerVersion(0.51, true, (get, set, remove) => {
        // Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("@offline_error"))) {
            return false;
        }

        let lastOfflineErrorStored = get("@offline_error", true);
        set("@store_error", { offlineError: lastOfflineErrorStored });
        remove("@offline_error");

        return false;
    }, (get, set, remove) => {
        // Downgrade
        let { offlineError } = get("@store_error", true) as {offlineError: string};
        offlineError && set("@offline_error", offlineError);
        remove("@store_error");

        return false;
    });

    registerVersion(0.52, true, (get, set, remove) => {
        // Upgrade
        let storedTrailerAttachments: IIdDictionary<IIdDictionary<ITrailerAttachment>> | null = null;

        Object.keys(localStorage).forEach((storageKey) => {
            if (isDriveKey(storageKey) && storageKey.includes("trailer_attachments")) {
                const userId: string = storageKey.split("_")[1];
                const trailerAttachments = get("trailer_attachments", true, userId) as IIdDictionary<ITrailerAttachment>;
                let userAttachments: IIdDictionary<ITrailerAttachment> = {};

                Object.keys(trailerAttachments).forEach(attachmentId => {
                    userAttachments[attachmentId] = trailerAttachments[attachmentId];
                });

                if (storedTrailerAttachments === null) {
                    storedTrailerAttachments = {};
                }
                storedTrailerAttachments[userId] = userAttachments;
            }
        });

        set("geotabDrive_@store_trailerAttachments", storedTrailerAttachments, undefined, true);
        storedTrailerAttachments && Object.keys(storedTrailerAttachments).forEach(userId => {
            remove("trailer_attachments", userId);
        });

        return false;
    }, (get, set, remove) => {
        // Downgrade

        if (!Object.keys(localStorage).find(key => isDriveKey(key) && key.includes("@store_trailerAttachments"))) {
            return false;
        }

        let trailerAttachments = get("@store_trailerAttachments", true) as IIdDictionary<IIdDictionary<ITrailerAttachment>>;

        Object.keys(trailerAttachments).forEach((userId) => {
            set("trailer_attachments", trailerAttachments[userId], userId);
        });

        remove("@store_trailerAttachments");

        return false;
    });

    // geotabDrive_<userId>_shipments --> geotabDrive_@store_shipments::<userId>::shipment
    registerVersion(0.53, true, (get, set, remove) => {
        //Upgrade
        let storeShipments: IIdDictionary<IIdDictionary<IShipment>> = {};
        Object.keys(localStorage).forEach((storageKey) => {
            if (isDriveKey(storageKey) && storageKey.includes("shipments")) {
                const userId: string = storageKey.split("_")[1];
                const shipments = get("shipments", true, userId) as IIdDictionary<IShipment>;
                storeShipments[userId] = {};
                if (shipments && Object.keys(shipments).length > 0) {
                    Object.keys(shipments).forEach(logId => {
                        storeShipments[userId][logId] = shipments[logId];
                    });
                }
            }
        });

        set("geotabDrive_@store_shipments", storeShipments, undefined, true);
        Object.keys(storeShipments).forEach(userId => {
            remove("shipments", userId);
        });

        return false;
    }, (get, set, remove) => {
        // Downgrade
        if (!Object.keys(localStorage).find(key => key.includes(StorageKeys.SHIPMENTS_KEY))) {
            return false;
        }

        let storeShipments = get(StorageKeys.SHIPMENTS_KEY, true) as IIdDictionary<IIdDictionary<IShipment>>;
        if (storeShipments && Object.keys(storeShipments).length > 0) {
            Object.keys(storeShipments).forEach((userId) => {
                set("shipments", storeShipments[userId], userId);
            });
            remove(StorageKeys.SHIPMENTS_KEY);
        }

        return false;
    });

    // geotabDrive_@suspiciousLogsSent --> geotabDrive_@store_suspicious_logs_sent
    registerVersion(0.54, true, (get, set, remove) => {
        // Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("@suspiciousLogsSent") && isDriveKey(key))) {
            return false;
        }

        let suspiciousLogsSentStored = get("@suspiciousLogsSent", true) as ISuspiciousLogsSent;
        set("@store_suspiciousLogsSent", suspiciousLogsSentStored);
        remove("@suspiciousLogsSent");

        return false;
    }, (get, set, remove) => {
        // Downgrade
        let suspiciousLogsSent = get("@store_suspiciousLogsSent", true) as ISuspiciousLogsSent;
        suspiciousLogsSent && set("@suspiciousLogsSent", suspiciousLogsSent);
        remove("@store_suspiciousLogsSent");

        return false;
    });

    // geotabDrive_<userId>_inspection_pin --> geotabDrive_@store_data_::userId::inspectionPin
    registerVersion(0.55, true, (get, set, remove) => {
        // Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("inspection_pin") && isDriveKey(key))) {
            return false;
        }

        let storeInspectionPin: IIdDictionary<string> = {};

        Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.includes("inspection_pin") && isDriveKey(storageKey)) {
                const userId: string = storageKey.split("_")[1];
                const inspectionPin = get("inspection_pin", false, userId);
                if (inspectionPin) {
                    storeInspectionPin[userId] = inspectionPin;
                }
            }
        });
        set(
            "@store_data",
            {
                ...get(
                    "@store_data",
                    true
                ),
                inspectionPin: storeInspectionPin
            }
        );

        Object.keys(storeInspectionPin).forEach(userId => {
            remove("inspection_pin", userId);
        });
        return false;
    }, (get, set, _) => {
        // Downgrade
        let { inspectionPin, ...storeData } = get("@store_data", true) as {inspectionPin: IIdDictionary<String> ; storeData: IDataStore};
        if (inspectionPin && Object.keys(inspectionPin).length > 0) {
            Object.keys(inspectionPin).forEach(userId => {
                set("inspection_pin", inspectionPin[userId], userId);
            });
        }
        set("@store_data", storeData);

        return false;
    });

    // geotabDrive_<userId>_alerts_availability --> geotabDrive_@store_data_::userId::alertsAvailability
    registerVersion(0.56, true, (get, set, remove) => {
        // Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("alerts_availability") && isDriveKey(key))) {
            return false;
        }

        let storeAlertsAvailability: IIdDictionary<IAlertsAvailability> = {};

        Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.includes("alerts_availability") && isDriveKey(storageKey)) {
                const userId: string = storageKey.split("_")[1];
                const alertAvailability = get("alerts_availability", true, userId) as IIdDictionary<IAlertsAvailability>;
                storeAlertsAvailability[userId] = alertAvailability[userId];
            }
        });

        set(
            "@store_data",
            {
                ...get(
                    "@store_data",
                    true
                ),
                alertsAvailability: storeAlertsAvailability
            }
        );

        Object.keys(storeAlertsAvailability).forEach(userId => {
            remove("alerts_availability", userId);
        });
        return false;
    }, (get, set, _) => {
        // Downgrade
        let { alertsAvailability, ...storeData } = get("@store_data", true) as {alertsAvailability: IIdDictionary<IAlertsAvailability> ; storeData: IDataStore};
        if (alertsAvailability && Object.keys(alertsAvailability).length > 0) {
            Object.keys(alertsAvailability).forEach(userId => {
                let storeAlertsAvailability = {};
                storeAlertsAvailability[userId]  = alertsAvailability[userId];
                set("alerts_availability", storeAlertsAvailability, userId);
            });
        }
        set("@store_data", storeData);

        return false;
    });

    // active --> geotabDrive_@store_activeUsers
    registerVersion(0.57, true, (get, set) => {
        //Upgrade
        const storageKeyName = "@active";
        const storageKeyWithPrefixName = `geotabDrive_${ storageKeyName }`;
        if (Object.keys(localStorage).indexOf(storageKeyWithPrefixName) === -1) {
            return false;
        }

        let activeUsers: string[] = [];
        let oldActiveUsersStr = get(storageKeyWithPrefixName, false, undefined, true);
        if (oldActiveUsersStr) {
            const parsed = JSON.parse(oldActiveUsersStr);
            activeUsers = Array.isArray(parsed) ? parsed : [];
            set(StorageKeys.ACTIVE_USERS_KEY, activeUsers);
        }

        return false;
    }, (get, set, remove) => {
        // Downgrade
        const storageKeyName = "@active";
        let storeKey = `geotabDrive_${ StorageKeys.ACTIVE_USERS_KEY }`;
        if (!Object.keys(localStorage).find(key => key === storeKey)) {
            return false;
        }

        const activeUsers = get(StorageKeys.ACTIVE_USERS_KEY, true) as string[];
        set(`geotabDrive_${ storageKeyName }`, JSON.stringify(activeUsers), undefined, true);
        remove(StorageKeys.ACTIVE_USERS_KEY);

        return false;
    });

    // Every upgrade >= 0.58 should provide test data for the upgrade and downgrade functions in storageUpgradesTest.ts

    // driveAPI_Credentials_{user} --> geotabDrive_@store_driveApiCredentials::<userName>::ICredentialsWithServer
    registerVersion(0.58, true, (get, set) => {
        //Upgrade
        const keyName = "driveAPI_Credentials";

        if (!Object.keys(localStorage).find(key => key.includes(keyName))) {
            return false;
        }

        let storeDriveApiCredentials: IIdDictionary<IIdDictionary<ICredentialsWithServer>> = {};
        Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.includes(keyName)) {
                const userName: string = storageKey.split(`${ keyName }_`)[1];
                const credentials = get(`${ keyName }_${ userName }`, true, undefined, true) as IIdDictionary<ICredentialsWithServer>;
                if (credentials && Object.keys(credentials).length > 0) {
                    storeDriveApiCredentials[userName] = credentials;
                }
            }
        });

        set(`geotabDrive_${ StorageKeys.DRIVE_API_CREDENTIALS_KEY }`, storeDriveApiCredentials, undefined, true);

        return false;
    }, (get, set, remove) => {
        // Downgrade
        if (!Object.keys(localStorage).some(key => key.includes(StorageKeys.DRIVE_API_CREDENTIALS_KEY))) {
            return false;
        }
        const credentials = get(StorageKeys.DRIVE_API_CREDENTIALS_KEY, true) as IIdDictionary<IIdDictionary<ICredentialsWithServer>>;
        remove(StorageKeys.DRIVE_API_CREDENTIALS_KEY);

        if (credentials && Object.keys(credentials).length > 0) {
            Object.keys(credentials).forEach(userId => {
                set(`driveAPI_Credentials_${ userId }`, credentials[userId], undefined, true);
            });
        }

        return false;
    });

    // Duplicate values of certain keys to be stored under old key name as well
    // geotabDrive_@store_driveApiCredentials::<userName>::ICredentialsWithServer --> driveAPI_Credentials_{user}
    // geotabDrive_@store_activeUsers --> geotabDrive_@active
    registerVersion(0.59, true, (get, set) => {
        let localStorageKeys = Object.keys(localStorage);

        if (localStorageKeys.some(key => key.includes(StorageKeys.ACTIVE_USERS_KEY))) {
            const activeUsers = get(StorageKeys.ACTIVE_USERS_KEY, true) as string[];
            set("geotabDrive_@active", JSON.stringify(activeUsers), undefined, true);
        }

        if (localStorageKeys.some(key => key.includes(StorageKeys.DRIVE_API_CREDENTIALS_KEY))) {
            const credentials = get(StorageKeys.DRIVE_API_CREDENTIALS_KEY, true) as IIdDictionary<IIdDictionary<ICredentialsWithServer>>;
            if (credentials) {
                Object.keys(credentials).forEach(userId => {
                    set(`driveAPI_Credentials_${ userId }`, credentials[userId], undefined, true);
                });
            }
        }

        return false;
    });


    // geotabDrive_{user}_cache_{cacheName} --> geotabDrive_@store_cache::<userName>::ICache
    registerVersion(0.60, true, (get, set, remove) => {
        //Upgrade
        const keyName = "_cache_";
        if (!Object.keys(localStorage).find(key => key.includes(keyName) && isDriveKey(key))) {
            return false;
        }

        const cacheNameMap: { [key in string]: keyof ICache } = {
            "device": "device",
            "defectlist": "defectList",
            "trailer_attachment": "trailerAttachment",
            "user": "user",
            "trailer": "trailer"
        };

        let cachesToRemove: [string, string][] = [];
        let reduxCache: IIdDictionary<ICache> = {};
        Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.includes(keyName)) {
                const userId = storageKey.split(`_`)[1];
                const cacheName = storageKey.split(keyName)[1];
                const cacheData = get(`geotabDrive_${ userId }${ keyName }${ cacheName }`, true, undefined, true);
                const newCacheName: keyof ICache = cacheNameMap[cacheName];
                cachesToRemove.push([userId, cacheName]);
                if (cacheData && Object.keys(cacheData).length > 0) {
                    reduxCache[userId] = {
                        ...reduxCache[userId],
                        [newCacheName]: cacheData
                    };
                }
            }
        });
        cachesToRemove.forEach(value => {
            remove(`cache_${ value[1] }`, value[0], false);
        });
        set(`geotabDrive_${ StorageKeys.CACHE_KEY }`, reduxCache, undefined, true);
        return false;
    }, (get, set, remove) => {
        // Downgrade
        const keyName = "cache";
        if (!Object.keys(localStorage).some(key => key.includes(StorageKeys.CACHE_KEY))) {
            return false;
        }

        const cacheNameMap: { [key in keyof ICache]: string } = {
            "device": "device",
            "defectList": "defectlist",
            "trailerAttachment": "trailer_attachment",
            "user": "user",
            "trailer": "trailer",
            "trailerDevice": "trailerDevice",
            "trailerAsDevice": "trailerAsDevice",
            "deviceLink": "deviceLink"
        };

        const reduxCache = get(StorageKeys.CACHE_KEY, true) as IIdDictionary<ICache>;
        remove(StorageKeys.CACHE_KEY);
        if (reduxCache && Object.keys(reduxCache).length > 0) {
            Object.keys(reduxCache).forEach(userId => {
                const userReduxCache = reduxCache[userId];
                Object.keys(userReduxCache).forEach(reduxCacheName => {
                    const newKey = cacheNameMap[reduxCacheName];
                    const value = userReduxCache[reduxCacheName];
                    set(`${ keyName }_${ newKey }`, value, userId, false);
                });
            });
        }
        return false;
    });

    // cleanup upgrade for timeZoneInfo nested dict
    registerVersion(0.61, false, (get, set, remove) => {
        // If no key exit early
        if (!Object.keys(localStorage).find(key => isDriveKey(key) && key.includes(StorageKeys.TIME_ZONE_INFO_KEY))) {
            return false;
        }

        const currentTimeZoneInfoStore = get<{}>(StorageKeys.TIME_ZONE_INFO_KEY, true, undefined, false);
        if (currentTimeZoneInfoStore && currentTimeZoneInfoStore.hasOwnProperty("@store")) {
            // Remove nested dictionary
            delete currentTimeZoneInfoStore["@store"];
            remove(StorageKeys.TIME_ZONE_INFO_KEY);
            set(`geotabDrive_${ StorageKeys.TIME_ZONE_INFO_KEY }`, currentTimeZoneInfoStore, undefined, true);
        }

        return false;
    }, undefined);

    /**
     * geotabDrive_@systemSettings <--> geotabDrive_@store_systemSettings
     */
    registerVersion(0.62, false, (get, set, remove) => {
        // Upgrade
        let systemSettings: ISystemSettings = defaultSystemSettings;
        if (!Object.keys(localStorage).find(key => key.includes("@systemSettings") && isDriveKey(key))) {
            // If no SystemSettings data, just set it to default
            set("@store_systemSettings", defaultSystemSettings);
            return false;
        }

        let systemSettingsFromStorage = get("@systemSettings", true) as ISystemSettings;
        Object.keys(defaultSystemSettings).forEach(key => {
            // For each key in System Settings replace the default value with the value from storage if it exists.
            if (systemSettingsFromStorage[key]) {
                systemSettings[key] = systemSettingsFromStorage[key];
            }
        });

        remove("@systemSettings");
        set("@store_systemSettings", systemSettings);
        return false;
    }, (get, set, remove) => {
        // Downgrade
        if (!Object.keys(localStorage).find(key => key.includes("@store_systemSettings") && isDriveKey(key))) {
            return false;
        }

        // Should be a 1-1 mapping going back, at least for all relevant values.
        let storeSystemSettings = get("@store_systemSettings", true) as ISystemSettings;
        remove("@store_systemSettings");
        set("@systemSettings", storeSystemSettings);
        return false;
    });

    /**
     * Remove incorrect 'key' property from images dictionary
     */
    registerVersion(0.63, false,  (get, set) => {
        // Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("@store_data") && isDriveKey(key))) {
            return false;
        }

        let { images, ...storeData } = get("@store_data", true) as {images: IImages};
        if (images && images.hasOwnProperty("key")) {
            delete images["key"];
            set("@store_data", { ...storeData, images });
        }

        return false;
    }, undefined);

    registerVersion(0.64, false, (get, set, remove) => {
        //Upgrade
        const vehicleHistory: IVehicleHistoryStore = { currentVehicle: null, previousVehicle: null };
        let currentVehicle: TVehicle;
        let previousVehicle: TVehicle;

        const LOCAL_STORAGE_CURRENT_VEHICLE_KEY = "@currentVehicle";
        if (StorageBase.keyExist(LOCAL_STORAGE_CURRENT_VEHICLE_KEY)){
            currentVehicle = get(LOCAL_STORAGE_CURRENT_VEHICLE_KEY, true);
            vehicleHistory.currentVehicle = currentVehicle
                ? currentVehicle
                : defaultVehicleHistory.currentVehicle;
            remove(LOCAL_STORAGE_CURRENT_VEHICLE_KEY);
        }

        const LOCAL_STORAGE_PREVIOUS_VEHICLE_KEY = "@previousVehicle";
        if (StorageBase.keyExist(LOCAL_STORAGE_PREVIOUS_VEHICLE_KEY)){
            previousVehicle = get(LOCAL_STORAGE_PREVIOUS_VEHICLE_KEY, true);
            vehicleHistory.previousVehicle = previousVehicle
                ? previousVehicle
                : defaultVehicleHistory.previousVehicle;
            remove(LOCAL_STORAGE_PREVIOUS_VEHICLE_KEY);
        }

        set(StorageKeys.VEHICLE_HISTORY_KEY, vehicleHistory);
        return false;
    }, (get, set, remove) => {
        // Downgrade
        const LOCAL_STORAGE_CURRENT_VEHICLE_KEY = "@currentVehicle";
        const LOCAL_STORAGE_PREVIOUS_VEHICLE_KEY = "@previousVehicle";

        if (StorageBase.keyExist(StorageKeys.VEHICLE_HISTORY_KEY)) {
            let vehicleHistory: IVehicleHistoryStore | null = get(StorageKeys.VEHICLE_HISTORY_KEY, true);
            let currentVehicle = vehicleHistory && vehicleHistory.currentVehicle
                ? vehicleHistory.currentVehicle
                : null;
            let previousVehicle = vehicleHistory && vehicleHistory.previousVehicle
                ? vehicleHistory.previousVehicle
                : null;
            remove(StorageKeys.VEHICLE_HISTORY_KEY);
            set(LOCAL_STORAGE_CURRENT_VEHICLE_KEY, currentVehicle);
            set(LOCAL_STORAGE_PREVIOUS_VEHICLE_KEY, previousVehicle);
        }
        return false;
    });

    registerVersion(0.65, false, (get, set) => {
        //Upgrade
        const keyName = "store_shipments";
        if (!Object.keys(localStorage).find(key => key.includes(keyName) && isDriveKey(key))) {
            return false;
        }
        const shipmentsByDevice: IIdDictionary<IIdDictionary<IShipment>> = {};
        Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.includes(keyName)) {
                const currentShipments = get("geotabDrive_@" + keyName, true, undefined, true) as IIdDictionary<IIdDictionary<IShipment>>;
                Object.keys(currentShipments).forEach(driverId => {
                    const driverShipments = currentShipments[driverId];
                    Object.keys(driverShipments).forEach(shipmentId => {
                        const shipment = driverShipments[shipmentId];
                        if (typeof (shipment.device) === "object" && shipment.device.id) {
                            const deviceId = shipment.device.id;
                            if (!shipmentsByDevice[deviceId]) {
                                shipmentsByDevice[deviceId] = {};
                            }
                            shipmentsByDevice[deviceId][shipment.id] = shipment;
                        }
                    });
                });
            }
        });
        set(`geotabDrive_@${ keyName }`, shipmentsByDevice, undefined, true);
        return false;
    }, (get, set) => {
        // Downgrade
        const keyName = "store_shipments";
        if (!Object.keys(localStorage).find(key => key.includes(keyName) && isDriveKey(key))) {
            return false;
        }
        const shipmentsByUser: IIdDictionary<IIdDictionary<IShipment>> = {};
        Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.includes(keyName)) {
                const currentShipments = get("geotabDrive_@" + keyName, true, undefined, true) as IIdDictionary<IIdDictionary<IShipment>>;
                Object.keys(currentShipments).forEach(deviceId => {
                    const deviceShipments = currentShipments[deviceId];
                    Object.keys(deviceShipments).forEach(shipmentId => {
                        const shipment = deviceShipments[shipmentId];
                        if (typeof (shipment.driver) === "object" && shipment.driver.id) {
                            const driverId = shipment.driver.id;
                            if (!shipmentsByUser[driverId]) {
                                shipmentsByUser[driverId] = {};
                            }
                            shipmentsByUser[driverId][shipment.id] = shipment;
                        }
                    });
                });
            }
        });
        set(`geotabDrive_@${ keyName }`, shipmentsByUser, undefined, true);
        return false;
    });

    // geotabDrive_@driver --> geotabDrive_@store_driverSeatUser
    registerVersion(0.66, false, (get, set, remove) => {
        //Upgrade
        const keyName = "geotabDrive_@driver";

        if (!Object.keys(localStorage).find(key => key.includes(keyName))) {
            return false;
        }
        Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.includes(keyName)) {
                const driverSeatUser = get(`${ keyName }`, false, undefined, true);
                remove("@driver");
                set(`geotabDrive_${ StorageKeys.DRIVER_SEAT_USER }`, driverSeatUser, undefined, true);
            }
        });

        return false;
    }, (get, set, remove) => {
        // Downgrade
        const keyName = "geotabDrive_@driver";
        if (!Object.keys(localStorage).some(key => key.includes(StorageKeys.DRIVER_SEAT_USER))) {
            return false;
        }
        const driverSeatUser = get(StorageKeys.DRIVER_SEAT_USER, false);
        remove("@store_driverSeatUser");
        if (driverSeatUser) {
            set(keyName, driverSeatUser, undefined, true);
        }
        return false;
    });

    // geotabDrive_@instance_id --> geotabDrive_@store_data::instanceId
    registerVersion(0.67, false, (get, set, remove) => {
        //Upgrade
        if (!Object.keys(localStorage).find(key => key.includes("@instance_id") && isDriveKey(key))) {
            return false;
        }

        const instanceId = get("@instance_id", false);
        remove("@instance_id");
        set(
            "@store_data",
            {
                ...get(
                    "@store_data",
                    true
                ),
                instanceId: instanceId
            }
        );

        return false;
    }, (get, set, _1) => {
        // Downgrade
        let { instanceId, ...storeData } = get("@store_data", true) as {instanceId: string; storeData: IDataStore};
        instanceId && set("@instance_id", instanceId);
        set("@store_data", storeData);

        return false;
    });

    /**
     * Remove 'unidentifiedHos' from geotabDrive_@store_feedVersions for all users
     */
    registerVersion(0.68, false, (get, set) => {
        // Upgrade
        if (!Object.keys(localStorage).find(key => isDriveKey(key) && key.includes("@store_feedVersions"))) {
            return false;
        }

        let feedVersions = get("@store_feedVersions", true) as IIdDictionary<IIdDictionary<string | IIdDictionary<string>>>;
        if (!feedVersions || typeof feedVersions !== "object") {
            return false;
        }

        let hasChanges = false;
        Object.keys(feedVersions).forEach((userId) => {
            let userFeedVersions = feedVersions[userId];
            if (userFeedVersions && typeof userFeedVersions === "object" && userFeedVersions.hasOwnProperty("unidentifiedHos")) {
                delete userFeedVersions.unidentifiedHos;
                hasChanges = true;
            }
        });

        if (hasChanges) {
            set("geotabDrive_@store_feedVersions", feedVersions, undefined, true);
        }

        return false;
    });

    // Remove orphaned @store_savedUserNames left by the removed DRIVE.ENABLE_SAVE_USERNAMES feature
    registerVersion(0.69, false, (_1, _2, remove) => {
        remove("@store_savedUserNames");
        return false;
    });

    return [upgradesDict, downgradesDict, versionsToRunForEachUser];
};

export default storageUpgrades;